<template>
  <div class="d-flex flex-column flex-grow-1">
    <div v-if="users.length > 0" class="user-list-container flex-grow-1">
      <div class="user-list-header d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
        <span class="text-muted font-weight-bold">
          {{ filteredUsersCount }} {{ $t("users_found") }}
        </span>
        <span v-if="searchQuery" class="badge badge-light-primary">
          {{ $t("filtered") }}
        </span>
      </div>

      <div class="user-list-scrollable">
        <div
            v-for="user in users"
            :key="user.id"
            class="user-item d-flex align-items-center px-3 py-2 border-bottom"
            :class="{ 'selected': isSelected(user.id) }"
            @click="handleUserClick(user.id)"
        >
          <b-form-checkbox
              v-model="localSelectedRecipients"
              :value="user.id"
              class="mr-3"
              @click.native.stop
          ></b-form-checkbox>

          <div class="user-info flex-grow-1">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="mb-0 font-weight-bold">{{ user.full_name }}</h6>
                <div class="text-muted small">{{ user.email }}</div>
              </div>
              <span class="badge" :class="getRoleBadgeClass(user.role)">
                {{ $t(user.role) }}
              </span>
            </div>
          </div>

          <div class="user-lang ml-3">
            <span class="badge badge-light-secondary text-uppercase">{{ user.language }}</span>
          </div>
        </div>

        <div v-if="users.length === 0" class="text-center py-5">
          <feather-icon icon="SearchIcon" size="48" class="text-muted mb-2"/>
          <p class="text-muted">{{ $t("no_matches_found") }}</p>
        </div>
      </div>
    </div>

    <div
        v-else-if="hasSelectedRoles && !loading"
        class="empty-state text-center py-5 bg-light rounded flex-grow-1 d-flex flex-column justify-content-center"
    >
      <feather-icon icon="UserXIcon" size="48" class="text-muted mb-2"/>
      <p class="text-muted">{{ $t("no_users_found") }}</p>
    </div>

    <div
        v-else
        class="empty-state text-center py-5 border rounded border-dashed flex-grow-1 d-flex flex-column justify-content-center"
    >
      <feather-icon icon="ArrowLeftIcon" size="48" class="text-muted mb-2 d-none d-md-inline-block"/>
      <p class="text-muted">{{ $t("select_group_to_start") }}</p>
    </div>
  </div>
</template>

<script>
import {
  BFormCheckbox,
} from "bootstrap-vue";

export default {
  name: "EmailUserList",

  components: {
    BFormCheckbox,
  },

  props: {
    users: {
      type: Array,
      required: true,
    },
    selectedRecipients: {
      type: Array,
      required: true,
    },
    filteredUsersCount: {
      type: Number,
      required: true,
    },
    searchQuery: {
      type: String,
      default: "",
    },
    hasSelectedRoles: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    localSelectedRecipients: {
      get() {
        return this.selectedRecipients;
      },
      set(value) {
        // Keep the original format from the checkbox
        this.$emit('update:selectedRecipients', value);
      }
    }
  },

  methods: {
    /**
     * Extract numeric ID from string IDs like 'u_3', 'd_4', 'p_5' -> 3, 4, 5
     */
    getNumericId(userId) {
      if (typeof userId === 'string' && userId.includes('_')) {
        const parts = userId.split('_');
        return parseInt(parts[parts.length - 1]); // Get the last part after underscore
      }
      return Number(userId);
    },

    /**
     * Check if user is selected
     */
    isSelected(userId) {
      const numericUserId = this.getNumericId(userId);

      return this.selectedRecipients.some(id => {
        const numericSelectedId = this.getNumericId(id);
        return numericSelectedId === numericUserId;
      });
    },

    /**
     * Handle user row click
     */
    handleUserClick(userId) {
      const currentSelection = [...this.selectedRecipients];
      const index = currentSelection.findIndex(id => {
        const numericId = this.getNumericId(id);
        const numericUserId = this.getNumericId(userId);
        return numericId === numericUserId;
      });

      if (index > -1) {
        currentSelection.splice(index, 1);
      } else {
        currentSelection.push(userId);
      }

      this.$emit('update:selectedRecipients', currentSelection);
    },

    /**
     * Get role badge class
     */
    getRoleBadgeClass(role) {
      switch (role) {
        case 'buyer': return 'badge-light-info';
        case 'seller': return 'badge-light-success';
        case 'partner': return 'badge-light-primary';
        case 'debtor': return 'badge-light-warning';
        default: return 'badge-light-secondary';
      }
    },
  },
};
</script>

<style scoped>
.user-list-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  border: 1px solid #e9ecef;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 450px;
}

.user-list-header {
  background: #fcfcfc;
}

.user-list-scrollable {
  overflow-y: auto;
  flex-grow: 1;
}

.user-item {
  transition: all 0.2s;
  cursor: pointer;
}

.user-item:hover {
  background-color: #f8f9fa;
}

.user-item.selected {
  background-color: #f0f7ff;
}

.border-dashed {
  border-style: dashed !important;
}

/* Badge styles */
.badge-light-primary { background-color: #e0e7ff; color: #4338ca; }
.badge-light-success { background-color: #dcfce7; color: #15803d; }
.badge-light-info { background-color: #e0f2fe; color: #0369a1; }
.badge-light-warning { background-color: #fef3c7; color: #92400e; }
.badge-light-secondary { background-color: #f3f4f6; color: #374151; }
</style>
